import { Button } from "@chakra-ui/button";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/modal";
import {
  FormControl,
  FormLabel,
  Select,
  Spinner,
  Center,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";

interface UpdateCardMobileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (mobileNumber: string) => Promise<void>;
  onFetchMobileNumbers: () => Promise<string[]>;
  currentMobileNumber?: string;
}

export const UpdateCardMobileDialog = ({
  isOpen,
  onClose,
  onConfirm,
  onFetchMobileNumbers,
  currentMobileNumber = "",
}: UpdateCardMobileDialogProps) => {
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileNumbers, setMobileNumbers] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingNumbers, setIsFetchingNumbers] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const fetchNumbers = async () => {
      if (isOpen) {
        setMobileNumber(currentMobileNumber);
        setIsLoading(false);
        setIsFetchingNumbers(true);

        try {
          const numbers = await onFetchMobileNumbers();
          setMobileNumbers(numbers);
          setIsFetchingNumbers(false);
        } catch (error) {
          setIsFetchingNumbers(false);
        }
      }
    };

    fetchNumbers();
  }, [isOpen, currentMobileNumber, onFetchMobileNumbers]);

  const handleConfirm = async () => {
    if (!mobileNumber) {
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm(mobileNumber);
      onClose();
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      leastDestructiveRef={cancelRef}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Update Mobile Number</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            {isFetchingNumbers && (
              <Center py={4}>
                <Spinner />
              </Center>
            )}
            {!isFetchingNumbers && (
              <FormControl>
                <FormLabel>Mobile Number</FormLabel>
                <Select
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  placeholder="Select mobile number"
                  isDisabled={isLoading}
                >
                  {mobileNumbers.map((number) => (
                    <option key={number} value={number}>
                      {number}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="cherryButton"
              ml={3}
              onClick={handleConfirm}
              isDisabled={isLoading || isFetchingNumbers}
              isLoading={isLoading}
            >
              Update
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
